/**
 * DesignerCanvas — Center panel of the Visual Workspace Designer.
 *
 * Displays the workspace views as tabs, with drop zones for panels.
 * Each panel renders a visual representation (grid columns, form fields, etc.)
 */
import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import TableChartIcon from '@mui/icons-material/TableChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SpeedIcon from '@mui/icons-material/Speed';
import BarChartIcon from '@mui/icons-material/BarChart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExtensionIcon from '@mui/icons-material/Extension';
import LinkIcon from '@mui/icons-material/Link';
import { useDroppable } from '@dnd-kit/core';

import {
  useDesignerState,
  useDesignerDispatch,
  useSelectedView,
} from './DesignerStateContext';

// ---------------------------------------------------------------------------
// Drop zone component
// ---------------------------------------------------------------------------
function DropZone({ id, label, acceptTypes, children, colors, minHeight = 80 }) {
  const { isOver, setNodeRef, active } = useDroppable({ id });

  const isValidDrop = active?.data?.current && acceptTypes.includes(active.data.current.type);
  const highlight = isOver && isValidDrop;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight,
        border: `2px dashed ${highlight ? '#22c55e' : 'rgba(255,255,255,0.1)'}`,
        borderRadius: 1,
        bgcolor: highlight ? 'rgba(34,197,94,0.08)' : 'transparent',
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: children ? 'flex-start' : 'center',
        p: 1,
        gap: 1,
      }}
    >
      {children || (
        <Typography variant="caption" sx={{ color: colors?.textSecondary || '#666' }}>
          {label || 'Drop here'}
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Panel card (visual representation of a panel on the canvas)
// ---------------------------------------------------------------------------
const PANEL_ICONS = {
  grid: TableChartIcon,
  'detail-grid': ListAltIcon,
  form: EditNoteIcon,
  kpi: SpeedIcon,
  chart: BarChartIcon,
};

const PANEL_LABELS = {
  grid: 'Grid',
  'detail-grid': 'Detail Grid',
  form: 'Form',
  kpi: 'KPI Card',
  chart: 'Chart',
};

function PanelCard({ panel, viewId, isSelected, colors, dispatch }) {
  const Icon = PANEL_ICONS[panel.type] || TableChartIcon;

  const handleSelect = useCallback(() => {
    dispatch({
      type: 'SET_SELECTION',
      selection: { type: 'panel', viewId, panelId: panel.panelId, itemId: null },
    });
  }, [dispatch, viewId, panel.panelId]);

  const handleRemove = useCallback((e) => {
    e.stopPropagation();
    dispatch({ type: 'REMOVE_PANEL', viewId, panelId: panel.panelId });
  }, [dispatch, viewId, panel.panelId]);

  return (
    <Box
      onClick={handleSelect}
      sx={{
        border: `1px solid ${isSelected ? '#22c55e' : 'rgba(255,255,255,0.12)'}`,
        borderRadius: 1,
        bgcolor: isSelected ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.02)',
        cursor: 'pointer',
        transition: 'all 0.15s',
        '&:hover': {
          borderColor: isSelected ? '#22c55e' : 'rgba(255,255,255,0.25)',
          bgcolor: isSelected ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.04)',
        },
        overflow: 'hidden',
      }}
    >
      {/* Panel header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          gap: 1,
        }}
      >
        <Icon sx={{ fontSize: 16, color: colors?.textSecondary || '#888' }} />
        <Typography variant="caption" sx={{ color: colors?.text || '#e0e0e0', fontWeight: 600, flex: 1 }}>
          {PANEL_LABELS[panel.type] || panel.type}
          {panel.entity && (
            <Typography component="span" sx={{ color: '#22c55e', ml: 0.5, fontSize: '0.7rem' }}>
              ({panel.entity})
            </Typography>
          )}
        </Typography>
        {panel.role && (
          <Chip
            label={panel.role}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              bgcolor: 'rgba(255,255,255,0.06)',
              color: colors?.textSecondary || '#888',
            }}
          />
        )}
        <Tooltip title="Remove panel">
          <IconButton
            size="small"
            onClick={handleRemove}
            sx={{ color: colors?.textSecondary || '#666', p: 0.25, opacity: 0.6, '&:hover': { opacity: 1, color: '#ef4444' } }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Panel body — visual representation */}
      <Box sx={{ p: 1.5, minHeight: 60 }}>
        {panel.type === 'grid' || panel.type === 'detail-grid' ? (
          <GridPanelPreview panel={panel} colors={colors} />
        ) : panel.type === 'form' ? (
          <FormPanelPreview panel={panel} colors={colors} />
        ) : panel.type === 'kpi' ? (
          <KpiPanelPreview panel={panel} colors={colors} />
        ) : panel.type === 'chart' ? (
          <ChartPanelPreview panel={panel} colors={colors} />
        ) : (
          <Typography variant="caption" sx={{ color: colors?.textSecondary || '#666' }}>
            {panel.type} panel
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Panel preview components
// ---------------------------------------------------------------------------
function GridPanelPreview({ panel, colors }) {
  const columns = panel.columns || [];
  if (columns.length === 0) {
    return (
      <Typography variant="caption" sx={{ color: colors?.textSecondary || '#666', fontStyle: 'italic' }}>
        Drag fields here to add columns
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {columns.map((col, idx) => (
        <Chip
          key={col.fieldName || `${col.fieldType}-${idx}`}
          label={col.fieldName || `[${col.fieldType || 'Unconfigured'}]`}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.65rem',
            bgcolor: col.fieldName ? 'rgba(255,255,255,0.06)' : 'rgba(255,165,0,0.15)',
            color: col.fieldName ? (colors?.text || '#ccc') : '#ffa500',
            border: col.fieldName ? 'none' : '1px dashed rgba(255,165,0,0.4)',
          }}
        />
      ))}
    </Box>
  );
}

function FormPanelPreview({ panel, colors }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {[1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            height: 8,
            width: `${60 + i * 10}%`,
            bgcolor: 'rgba(255,255,255,0.06)',
            borderRadius: 0.5,
          }}
        />
      ))}
      <Typography variant="caption" sx={{ color: colors?.textSecondary || '#666', fontStyle: 'italic', mt: 0.5 }}>
        {panel.entity ? `Form: ${panel.entity}` : 'Configure entity in properties'}
      </Typography>
    </Box>
  );
}

function KpiPanelPreview({ panel, colors }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <SpeedIcon sx={{ fontSize: 24, color: '#22c55e' }} />
      <Box>
        <Typography variant="caption" sx={{ color: colors?.textSecondary || '#888', display: 'block' }}>
          KPI Metric
        </Typography>
        <Typography variant="h6" sx={{ color: colors?.text || '#e0e0e0', lineHeight: 1 }}>
          --
        </Typography>
      </Box>
    </Box>
  );
}

function ChartPanelPreview({ panel, colors }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 40 }}>
      {[40, 65, 35, 80, 55, 70].map((h, i) => (
        <Box
          key={i}
          sx={{
            width: 12,
            height: `${h}%`,
            bgcolor: 'rgba(34,197,94,0.3)',
            borderRadius: '2px 2px 0 0',
          }}
        />
      ))}
      <Typography variant="caption" sx={{ color: colors?.textSecondary || '#888', ml: 1 }}>
        Chart
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Ref View Card — shows a component reference instead of editable panels
// ---------------------------------------------------------------------------
function RefViewCard({ view, isSelected, colors, dispatch }) {
  const handleSelect = useCallback(() => {
    dispatch({
      type: 'SET_SELECTION',
      selection: { type: 'view', viewId: view.viewId, panelId: null, itemId: null },
    });
  }, [dispatch, view.viewId]);

  const overrideCount = view.overrides ? Object.keys(view.overrides).length : 0;

  return (
    <Box
      onClick={handleSelect}
      sx={{
        border: `1px solid ${isSelected ? '#22c55e' : 'rgba(96,165,250,0.3)'}`,
        borderRadius: 1,
        bgcolor: isSelected ? 'rgba(34,197,94,0.06)' : 'rgba(96,165,250,0.04)',
        cursor: 'pointer',
        transition: 'all 0.15s',
        '&:hover': {
          borderColor: isSelected ? '#22c55e' : 'rgba(96,165,250,0.5)',
          bgcolor: isSelected ? 'rgba(34,197,94,0.08)' : 'rgba(96,165,250,0.08)',
        },
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          gap: 1,
        }}
      >
        <LinkIcon sx={{ fontSize: 16, color: '#60a5fa' }} />
        <Typography variant="caption" sx={{ color: colors?.text || '#e0e0e0', fontWeight: 600, flex: 1 }}>
          Component Reference
        </Typography>
        <Chip
          label="$ref"
          size="small"
          sx={{
            height: 18,
            fontSize: '0.6rem',
            bgcolor: 'rgba(96,165,250,0.15)',
            color: '#60a5fa',
            fontFamily: 'monospace',
          }}
        />
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ExtensionIcon sx={{ fontSize: 20, color: '#60a5fa' }} />
          <Box>
            <Typography sx={{ color: colors?.text || '#e0e0e0', fontSize: '0.85rem', fontWeight: 600 }}>
              {view.componentCode}
            </Typography>
            <Typography variant="caption" sx={{ color: colors?.textSecondary || '#888' }}>
              {view.componentType || 'VIEW'} component
            </Typography>
          </Box>
        </Box>

        {overrideCount > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#f59e0b', fontSize: '0.65rem' }}>
              {overrideCount} override{overrideCount > 1 ? 's' : ''} applied
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {Object.keys(view.overrides).map((key) => (
                <Chip
                  key={key}
                  label={key}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    bgcolor: 'rgba(245,158,11,0.1)',
                    color: '#f59e0b',
                    border: '1px solid rgba(245,158,11,0.2)',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Typography variant="caption" sx={{ color: colors?.textSecondary || '#666', fontStyle: 'italic', display: 'block', mt: 1 }}>
          This view is rendered from a reusable component. Select to edit overrides in the properties panel.
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// DesignerCanvas
// ---------------------------------------------------------------------------
export default function DesignerCanvas({ colors }) {
  const designer = useDesignerState();
  const dispatch = useDesignerDispatch();
  const { views, selection } = designer;

  // Active view tab index
  const activeViewIndex = (() => {
    if (selection.viewId) {
      const idx = views.findIndex((v) => v.viewId === selection.viewId);
      if (idx >= 0) return idx;
    }
    const defaultIdx = views.findIndex((v) => v.isDefault);
    return defaultIdx >= 0 ? defaultIdx : 0;
  })();

  const activeView = views[activeViewIndex] || null;

  const handleTabChange = useCallback(
    (_, newIndex) => {
      if (newIndex < views.length) {
        dispatch({
          type: 'SET_SELECTION',
          selection: { type: 'view', viewId: views[newIndex].viewId, panelId: null, itemId: null },
        });
      }
    },
    [dispatch, views]
  );

  const handleAddView = useCallback(() => {
    dispatch({ type: 'ADD_VIEW' });
  }, [dispatch]);

  const handleRemoveView = useCallback(
    (e, viewId) => {
      e.stopPropagation();
      dispatch({ type: 'REMOVE_VIEW', viewId });
    },
    [dispatch]
  );

  const handleCanvasClick = useCallback(
    (e) => {
      // Click on empty canvas area clears panel selection
      if (e.target === e.currentTarget) {
        dispatch({
          type: 'SET_SELECTION',
          selection: {
            type: 'view',
            viewId: activeView?.viewId || null,
            panelId: null,
            itemId: null,
          },
        });
      }
    },
    [dispatch, activeView]
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: colors?.background || '#0f0f1a',
      }}
    >
      {/* View tabs */}
      <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${colors?.border || '#333'}` }}>
        <Tabs
          value={activeViewIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            flex: 1,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0,
              px: 2,
              textTransform: 'none',
              fontSize: '0.8rem',
              color: colors?.textSecondary || '#888',
              '&.Mui-selected': { color: '#22c55e' },
            },
            '& .MuiTabs-indicator': { bgcolor: '#22c55e', height: 2 },
          }}
        >
          {views.map((v) => (
            <Tab
              key={v.viewId}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {v.isRef && <LinkIcon sx={{ fontSize: 12, color: '#60a5fa' }} />}
                  {v.label}
                  {v.isDefault && (
                    <Chip
                      label="default"
                      size="small"
                      sx={{ height: 14, fontSize: '0.55rem', bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e' }}
                    />
                  )}
                  {v.isRef && (
                    <Chip
                      label="ref"
                      size="small"
                      sx={{ height: 14, fontSize: '0.5rem', bgcolor: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}
                    />
                  )}
                  {views.length > 1 && (
                    <CloseIcon
                      sx={{ fontSize: 14, ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}
                      onClick={(e) => handleRemoveView(e, v.viewId)}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Tooltip title="Add view (tab)">
          <IconButton size="small" onClick={handleAddView} sx={{ color: colors?.textSecondary || '#888', mr: 1 }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Canvas area */}
      <Box
        onClick={handleCanvasClick}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {activeView && (
          <>
            {activeView.isRef ? (
              /* Ref view — show component reference card */
              <RefViewCard
                view={activeView}
                isSelected={selection.viewId === activeView.viewId}
                colors={colors}
                dispatch={dispatch}
              />
            ) : (
              /* Inline view — normal editable panels */
              <>
                {/* Filter zone */}
                <DropZone
                  id={`drop-filter-${activeView.viewId}`}
                  label="Drop fields here to create filter chips"
                  acceptTypes={['field-type']}
                  colors={colors}
                  minHeight={40}
                >
                  {activeView.filters?.chips?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', width: '100%' }}>
                      {activeView.filters.chips.map((chip, i) => (
                        <Chip
                          key={i}
                          label={chip.label || chip.value}
                          size="small"
                          color={chip.default ? 'primary' : 'default'}
                          onDelete={() => dispatch({ type: 'REMOVE_FILTER_CHIP', viewId: activeView.viewId, index: i })}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  )}
                </DropZone>

                {/* Panel zone */}
                <DropZone
                  id={`drop-panel-${activeView.viewId}`}
                  label="Drop panels here (Grid, Form, KPI, Chart...) or reusable components"
                  acceptTypes={['panel', 'field-type', 'component-ref']}
                  colors={colors}
                  minHeight={activeView.panels.length > 0 ? 0 : 120}
                >
                  {activeView.panels.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                      {activeView.panels.map((panel) => (
                        <PanelCard
                          key={panel.panelId}
                          panel={panel}
                          viewId={activeView.viewId}
                          isSelected={selection.panelId === panel.panelId}
                          colors={colors}
                          dispatch={dispatch}
                        />
                      ))}
                    </Box>
                  )}
                </DropZone>

                {/* Status bar zone */}
                <DropZone
                  id={`drop-statusbar-${activeView.viewId}`}
                  label="Status bar counters"
                  acceptTypes={['field-type']}
                  colors={colors}
                  minHeight={36}
                >
                  {activeView.statusBar?.counters?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', width: '100%' }}>
                      {activeView.statusBar.counters.map((c, i) => (
                        <Chip
                          key={i}
                          label={`${c.label}: --`}
                          size="small"
                          color={c.color || 'default'}
                          onDelete={() => dispatch({ type: 'REMOVE_COUNTER', viewId: activeView.viewId, index: i })}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  )}
                </DropZone>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
